export const IAR_FEE_TYPES = Object.freeze({
  FIXED_PERCENTAGE: "Fixed Percentage",
  TIERED: "Tiered",
  FLAT_FEE: "Flat Fee",
});

export const REQUEST_ACTIONS = Object.freeze({
  STRATEGY_CHANGE: "Strategy Change",
  REREGISTRATION: "Reregistration",
  RISK_TOLERANCE_UPDATE: "Risk Tolerance Update",
  MODEL_CHANGE: "Model Change",
  FEE_UPDATE: "Fee Update",
});

const cleanText = (value) => String(value ?? "").trim();
const key = (value) => cleanText(value).toLowerCase();

export function isStrategyChangeWorkflowAction(action) {
  const normalized = cleanText(action);
  return (
    normalized === REQUEST_ACTIONS.STRATEGY_CHANGE ||
    normalized === REQUEST_ACTIONS.REREGISTRATION
  );
}

const SINGLE_OWNER_REGISTRATIONS = new Set([
  "traditional ira",
  "rollover ira",
  "roth ira",
  "inherited ira",
  "inherited roth ira",
  "sep ira",
  "simple ira",
  "solo 401(k)",
  "individual",
  "individual tod",
  "custodial roth ira",
  "utma/ugma",
  "ugma/utma",
  "utma",
  "ugma",
]);

const UTMA_UGMA_REGISTRATIONS = new Set([
  "utma/ugma",
  "ugma/utma",
  "utma",
  "ugma",
]);

const BUSINESS_REGISTRATIONS = new Set([
  "business",
  "corporate",
  "corporation",
  "llc",
  "partnership",
  "sole proprietorship",
  "non-profit",
  "nonprofit",
]);

const SOLO_K_REGISTRATIONS = new Set([
  "solo(k)",
  "solo 401(k)",
  "solo 401k",
]);

export const MODEL_CHANGE_STRATEGY_RESELECTION_ERROR =
  "Fusion Managed Platform and Private Client Strategies (PCS) are not eligible for Model Change. Select an eligible Strategy before continuing.";

export const REGISTRATION_DETAIL_KINDS = Object.freeze({
  NONE: "none",
  MINOR: "minor",
  TRUST: "trust",
  ESTATE: "estate",
  BUSINESS: "business",
  SOLO_K: "soloK",
});

export const REGISTRATION_DETAIL_FIELD_DEFINITIONS = Object.freeze({
  custodianFullName: Object.freeze({
    label: "Custodian’s Full Name",
    placeholder: "Custodian’s Full Name",
    maxLength: 160,
  }),
  relationshipToPrimary: Object.freeze({
    label: "Relationship to Primary",
    placeholder: "i.e. Parent, Grandparent, Uncle/Aunt, etc.",
    maxLength: 100,
  }),
  trustName: Object.freeze({
    label: "Name of Trust",
    placeholder: "Name of Trust",
    maxLength: 160,
  }),
  decedentName: Object.freeze({
    label: "Decedent Name",
    placeholder: "Decedent's full name",
    maxLength: 160,
  }),
  decedentSsnLast4: Object.freeze({
    label: "Last 4 SSN",
    placeholder: "1234",
    maxLength: 4,
  }),
  relationshipToExecutor: Object.freeze({
    label: "Relationship to Primary Account Holder",
    placeholder: "i.e. Parent, Spouse, Sibling, etc.",
    maxLength: 100,
  }),
  businessName: Object.freeze({
    label: "Business Name",
    placeholder: "Legal Business Name",
    maxLength: 160,
  }),
  businessEinLast4: Object.freeze({
    label: "Last 4 Digits of EIN",
    placeholder: "1234",
    maxLength: 4,
  }),
  soloKQuestionnaire: Object.freeze({
    label: "Solo(k) Questionnaire",
  }),
});

export const REGISTRATION_DETAIL_FIELD_MAP = Object.freeze({
  [REGISTRATION_DETAIL_KINDS.NONE]: Object.freeze([]),
  [REGISTRATION_DETAIL_KINDS.MINOR]: Object.freeze([
    "custodianFullName",
    "relationshipToPrimary",
  ]),
  [REGISTRATION_DETAIL_KINDS.TRUST]: Object.freeze(["trustName"]),
  [REGISTRATION_DETAIL_KINDS.ESTATE]: Object.freeze([
    "decedentName",
    "decedentSsnLast4",
    "relationshipToExecutor",
  ]),
  [REGISTRATION_DETAIL_KINDS.BUSINESS]: Object.freeze([
    "businessName",
    "businessEinLast4",
  ]),
  [REGISTRATION_DETAIL_KINDS.SOLO_K]: Object.freeze([
    "soloKQuestionnaire",
  ]),
});

export const EMPTY_SOLO_K_QUESTIONNAIRE = Object.freeze({
  sponsoringBusinessName: "",
  ownerCount: "",
  employeeCount: "",
  secondParticipantIsSpouse: "",
  anticipatesEmployees: "",
  planType: "",
  existingPlanDocumentsProvided: false,
  ownershipEvidence: Object.freeze([]),
  certificationNoEmployees: false,
  certificationContributionLimits: false,
});

export const SOLO_K_SPOUSE_QUESTION =
  "Is the 2nd Owner/Employee the Account Owner's spouse?";

export const SOLO_K_OWNERSHIP_EVIDENCE_PROMPT =
  "(Please provide with client-signed paperwork)";

export const SOLO_K_PARTICIPANT_LIMIT_ALERT =
  "Solo(k) plans are limited to the business owner and their spouse. " +
  "Plans with more than two participants are not permitted. Please select " +
  "a different registration type.";

export const shouldAskSoloKSpouseQuestion = (questionnaire = {}) =>
  Number(questionnaire.ownerCount) > 1 ||
  Number(questionnaire.employeeCount) > 1;

export const exceedsSoloKParticipantLimit = (questionnaire = {}) =>
  Number(questionnaire.ownerCount) > 2 ||
  Number(questionnaire.employeeCount) > 2;

export const createEmptyRegistrationDetails = () => ({
  custodianFullName: "",
  relationshipToPrimary: "",
  trustName: "",
  businessName: "",
  businessEinLast4: "",
  decedentName: "",
  decedentSsnLast4: "",
  relationshipToExecutor: "",
  soloKQuestionnaire: {
    ...EMPTY_SOLO_K_QUESTIONNAIRE,
    ownershipEvidence: [],
  },
});

const getRegistrationType = (registration, registrationTypes = []) => {
  const registrationKey = key(registration);
  return (Array.isArray(registrationTypes) ? registrationTypes : []).find(
    (candidate) => key(candidate?.name) === registrationKey,
  );
};

export function getRegistrationDetailKind(
  registration,
  registrationTypes = [],
) {
  const registrationKey = key(registration);
  if (!registrationKey) return REGISTRATION_DETAIL_KINDS.NONE;
  if (
    UTMA_UGMA_REGISTRATIONS.has(registrationKey) ||
    registrationKey === "custodial roth ira"
  ) {
    return REGISTRATION_DETAIL_KINDS.MINOR;
  }
  if (
    registrationKey === "revocable trust" ||
    registrationKey === "irrevocable trust"
  ) {
    return REGISTRATION_DETAIL_KINDS.TRUST;
  }
  if (
    registrationKey === "estate" ||
    registrationKey.startsWith("estate ")
  ) {
    return REGISTRATION_DETAIL_KINDS.ESTATE;
  }
  if (
    SOLO_K_REGISTRATIONS.has(registrationKey) ||
    registrationKey.includes("solo(k)") ||
    registrationKey.includes("solo 401")
  ) {
    return REGISTRATION_DETAIL_KINDS.SOLO_K;
  }
  const registrationType = getRegistrationType(
    registration,
    registrationTypes,
  );
  if (
    key(registrationType?.category) === "business account" ||
    BUSINESS_REGISTRATIONS.has(registrationKey)
  ) {
    return REGISTRATION_DETAIL_KINDS.BUSINESS;
  }
  return REGISTRATION_DETAIL_KINDS.NONE;
}

export const getRegistrationDetailFieldKeys = (
  registration,
  registrationTypes = [],
) =>
  REGISTRATION_DETAIL_FIELD_MAP[
    getRegistrationDetailKind(registration, registrationTypes)
  ];

export const normalizeSoloKQuestionnaire = (value = {}) => {
  const normalized = {
    ...EMPTY_SOLO_K_QUESTIONNAIRE,
    ...(value && typeof value === "object" ? value : {}),
    sponsoringBusinessName: cleanText(value?.sponsoringBusinessName),
    ownerCount: cleanText(value?.ownerCount),
    employeeCount: cleanText(value?.employeeCount),
    secondParticipantIsSpouse: cleanText(
      value?.secondParticipantIsSpouse,
    ),
    anticipatesEmployees: cleanText(value?.anticipatesEmployees),
    planType: cleanText(value?.planType),
    existingPlanDocumentsProvided:
      value?.existingPlanDocumentsProvided === true,
    ownershipEvidence: Array.isArray(value?.ownershipEvidence)
      ? value.ownershipEvidence.map(cleanText).filter(Boolean)
      : [],
    certificationNoEmployees: value?.certificationNoEmployees === true,
    certificationContributionLimits:
      value?.certificationContributionLimits === true,
  };
  if (
    !shouldAskSoloKSpouseQuestion(normalized) ||
    !["Yes", "No"].includes(normalized.secondParticipantIsSpouse)
  ) {
    normalized.secondParticipantIsSpouse = "";
  }
  return normalized;
};

export function normalizeRegistrationDetails(
  source = {},
  registration,
  registrationTypes = [],
) {
  const details = createEmptyRegistrationDetails();
  const kind = getRegistrationDetailKind(registration, registrationTypes);

  if (kind === REGISTRATION_DETAIL_KINDS.MINOR) {
    details.custodianFullName = cleanText(
      source.custodianFullName || source.custodialAdultName,
    );
    details.relationshipToPrimary = cleanText(source.relationshipToPrimary);
  } else if (kind === REGISTRATION_DETAIL_KINDS.TRUST) {
    details.trustName = cleanText(source.trustName);
  } else if (kind === REGISTRATION_DETAIL_KINDS.ESTATE) {
    details.decedentName = cleanText(source.decedentName);
    details.decedentSsnLast4 = cleanText(source.decedentSsnLast4)
      .replace(/\D/g, "")
      .slice(0, 4);
    details.relationshipToExecutor = cleanText(
      source.relationshipToExecutor,
    );
  } else if (kind === REGISTRATION_DETAIL_KINDS.BUSINESS) {
    details.businessName = cleanText(source.businessName);
    details.businessEinLast4 = cleanText(source.businessEinLast4)
      .replace(/\D/g, "")
      .slice(0, 4);
  } else if (kind === REGISTRATION_DETAIL_KINDS.SOLO_K) {
    details.soloKQuestionnaire = normalizeSoloKQuestionnaire(
      source.soloKQuestionnaire,
    );
  }

  return { registrationDetailKind: kind, ...details };
}

const isSoloKQuestionnaireComplete = (questionnaire = {}) =>
  !!cleanText(questionnaire.sponsoringBusinessName) &&
  Number(questionnaire.ownerCount) > 0 &&
  questionnaire.employeeCount !== "" &&
  Number(questionnaire.employeeCount) >= 0 &&
  !exceedsSoloKParticipantLimit(questionnaire) &&
  (!shouldAskSoloKSpouseQuestion(questionnaire) ||
    ["Yes", "No"].includes(questionnaire.secondParticipantIsSpouse)) &&
  !!cleanText(questionnaire.anticipatesEmployees) &&
  !!cleanText(questionnaire.planType) &&
  (questionnaire.planType !== "Existing Plan" ||
    questionnaire.existingPlanDocumentsProvided === true) &&
  Array.isArray(questionnaire.ownershipEvidence) &&
  questionnaire.ownershipEvidence.length > 0 &&
  questionnaire.certificationNoEmployees === true &&
  questionnaire.certificationContributionLimits === true;

export function getRegistrationDetailValidationIssue(
  source = {},
  registration,
  registrationTypes = [],
  { required = true } = {},
) {
  const details = normalizeRegistrationDetails(
    source,
    registration,
    registrationTypes,
  );
  const activeKeys =
    REGISTRATION_DETAIL_FIELD_MAP[details.registrationDetailKind] ||
    REGISTRATION_DETAIL_FIELD_MAP[REGISTRATION_DETAIL_KINDS.NONE];
  const hasAnyValue = activeKeys.some((field) => {
    if (field === "soloKQuestionnaire") {
      return Object.values(details.soloKQuestionnaire).some((value) =>
        Array.isArray(value) ? value.length > 0 : !!value,
      );
    }
    return !!cleanText(details[field]);
  });
  if (!required && !hasAnyValue) return "";

  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.MINOR) {
    if (!details.custodianFullName) {
      return "Minor registrations require Custodian’s Full Name.";
    }
    if (!details.relationshipToPrimary) {
      return "Minor registrations require Relationship to Primary.";
    }
  }
  if (
    details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.TRUST &&
    !details.trustName
  ) {
    return "Trust registrations require Name of Trust.";
  }
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.ESTATE) {
    if (
      !details.decedentName ||
      !/^\d{4}$/.test(details.decedentSsnLast4) ||
      !details.relationshipToExecutor
    ) {
      return "Estate registrations require Decedent Name, a four-digit Decedent SSN Last 4, and Relationship to Primary Account Holder.";
    }
  }
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.BUSINESS) {
    if (
      !details.businessName ||
      !/^\d{4}$/.test(details.businessEinLast4)
    ) {
      return "Business registrations require Business Name and a four-digit EIN Last 4.";
    }
  }
  if (
    details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.SOLO_K &&
    exceedsSoloKParticipantLimit(details.soloKQuestionnaire)
  ) {
    return SOLO_K_PARTICIPANT_LIMIT_ALERT;
  }
  if (
    details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.SOLO_K &&
    !isSoloKQuestionnaireComplete(details.soloKQuestionnaire)
  ) {
    return "Solo(k) registrations require the complete Solo(k) Questionnaire.";
  }
  return "";
}

export function getRegistrationDetailReviewRows(
  source = {},
  registration,
  registrationTypes = [],
) {
  const details = normalizeRegistrationDetails(
    source,
    registration,
    registrationTypes,
  );
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.MINOR) {
    return [
      {
        label: REGISTRATION_DETAIL_FIELD_DEFINITIONS.custodianFullName.label,
        value: details.custodianFullName,
      },
      {
        label:
          REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary.label,
        value: details.relationshipToPrimary,
      },
    ].filter((row) => row.value);
  }
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.TRUST) {
    return [
      {
        label: REGISTRATION_DETAIL_FIELD_DEFINITIONS.trustName.label,
        value: details.trustName,
      },
    ].filter((row) => row.value);
  }
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.ESTATE) {
    return [
      {
        label: REGISTRATION_DETAIL_FIELD_DEFINITIONS.decedentName.label,
        value: details.decedentName,
      },
      {
        label: REGISTRATION_DETAIL_FIELD_DEFINITIONS.decedentSsnLast4.label,
        value: details.decedentSsnLast4
          ? `****${details.decedentSsnLast4}`
          : "",
      },
      {
        label:
          REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToExecutor.label,
        value: details.relationshipToExecutor,
      },
    ].filter((row) => row.value);
  }
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.BUSINESS) {
    return [
      {
        label: REGISTRATION_DETAIL_FIELD_DEFINITIONS.businessName.label,
        value: details.businessName,
      },
      {
        label: REGISTRATION_DETAIL_FIELD_DEFINITIONS.businessEinLast4.label,
        value: details.businessEinLast4
          ? `****${details.businessEinLast4}`
          : "",
      },
    ].filter((row) => row.value);
  }
  if (details.registrationDetailKind === REGISTRATION_DETAIL_KINDS.SOLO_K) {
    const questionnaire = details.soloKQuestionnaire;
    return [
      {
        label: "Name of Sponsoring Business",
        value: questionnaire.sponsoringBusinessName,
      },
      { label: "Number of Owners", value: questionnaire.ownerCount },
      {
        label: "Number of Non-Owner Employees",
        value: questionnaire.employeeCount,
      },
      {
        label: "2nd Owner/Employee Is Account Owner's Spouse",
        value: questionnaire.secondParticipantIsSpouse,
      },
      {
        label: "Anticipates Employees",
        value: questionnaire.anticipatesEmployees,
      },
      { label: "Plan Type", value: questionnaire.planType },
      {
        label: "Existing Plan Documents",
        value: questionnaire.existingPlanDocumentsProvided
          ? "Confirmed"
          : "",
      },
      {
        label: "Ownership Evidence",
        value: questionnaire.ownershipEvidence.join(", "),
      },
      {
        label: "No-Employee Certification",
        value: questionnaire.certificationNoEmployees ? "Confirmed" : "",
      },
      {
        label: "Contribution-Limit Certification",
        value: questionnaire.certificationContributionLimits
          ? "Confirmed"
          : "",
      },
    ].filter((row) => String(row.value ?? "").trim());
  }
  return [];
}

export function normalizeIarFeeType(value) {
  const normalized = key(value);
  if (normalized === "fixed percentage" || normalized === "fixed") {
    return IAR_FEE_TYPES.FIXED_PERCENTAGE;
  }
  if (normalized === "tiered") return IAR_FEE_TYPES.TIERED;
  if (normalized === "flat fee" || normalized === "flat dollar") {
    return IAR_FEE_TYPES.FLAT_FEE;
  }
  return "";
}

export function isValidIarPercentage(value, { max = Infinity } = {}) {
  const raw = cleanText(value).replace(/%$/, "").trim();
  if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(raw)) return false;
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric >= 0 && numeric <= max;
}

export function formatIarPercentage(value) {
  const raw = cleanText(value).replace(/%$/, "").trim();
  if (!isValidIarPercentage(raw)) return raw;
  const [whole, fraction = ""] = raw.split(".");
  return `${whole}.${fraction.padEnd(2, "0")}`;
}

export function normalizeWholePercentageInput(value) {
  const raw = cleanText(value).replace(/%$/, "").trim();
  if (raw === "") return "";
  return /^\d+$/.test(raw) ? raw : null;
}

export function isCustodialRothAvailableForRequest(
  custodian = "",
  { requestType = "", existingRequestAction = "" } = {},
) {
  const capabilities = getRequestCapabilities(
    requestType,
    existingRequestAction,
  );
  const updateWithoutCustodian =
    capabilities.isExisting &&
    [
      REQUEST_ACTIONS.RISK_TOLERANCE_UPDATE,
      REQUEST_ACTIONS.MODEL_CHANGE,
      REQUEST_ACTIONS.FEE_UPDATE,
    ].includes(capabilities.action);
  return updateWithoutCustodian || key(custodian).includes("charles schwab");
}

export function getRegistrationRules(
  registration,
  custodian = "",
  requestContext = {},
) {
  const registrationKey = key(registration);
  const isUtmaUgma = UTMA_UGMA_REGISTRATIONS.has(registrationKey);
  const isCustodialRoth = registrationKey === "custodial roth ira";
  const isRevocableTrust = registrationKey === "revocable trust";
  const isIrrevocableTrust = registrationKey === "irrevocable trust";
  const detailKind = getRegistrationDetailKind(
    registration,
    requestContext.registrationTypes || [],
  );

  return {
    isUtmaUgma,
    isCustodialRoth,
    isMinorRegistration: isUtmaUgma || isCustodialRoth,
    isTrust: isRevocableTrust || isIrrevocableTrust,
    custodianFieldLabel:
      detailKind === REGISTRATION_DETAIL_KINDS.MINOR
        ? REGISTRATION_DETAIL_FIELD_DEFINITIONS.custodianFullName.label
        : "",
    relationshipToPrimaryLabel:
      REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary.label,
    relationshipToPrimaryHint:
      REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary.placeholder,
    allowsSecondAccountHolder: !SINGLE_OWNER_REGISTRATIONS.has(registrationKey),
    asksTrustName: isRevocableTrust || isIrrevocableTrust,
    asksEstateDetails: detailKind === REGISTRATION_DETAIL_KINDS.ESTATE,
    asksBusinessDetails: detailKind === REGISTRATION_DETAIL_KINDS.BUSINESS,
    asksSoloKQuestionnaire: detailKind === REGISTRATION_DETAIL_KINDS.SOLO_K,
    detailKind,
    detailFields: REGISTRATION_DETAIL_FIELD_MAP[detailKind],
    allowsHouseholding: !isIrrevocableTrust,
    isAvailableForCustodian:
      !isCustodialRoth ||
      isCustodialRothAvailableForRequest(custodian, requestContext),
  };
}

export function getIspRegistrationTypes(
  registrationTypes = [],
  custodian = "",
  requestContext = {},
) {
  const base = Array.isArray(registrationTypes)
    ? registrationTypes.filter((registration) => registration?.name)
    : [];
  const withoutSynthetic = base.filter(
    (registration) =>
      key(registration.name) !== "custodial roth ira" &&
      key(registration.name) !== "donor advised fund",
  );
  const donorAdvisedFund =
    base.find((registration) => key(registration.name) === "donor advised fund") ||
    {
      name: "Donor Advised Fund",
      category: "Other",
      order: 9998,
    };
  const result = [...withoutSynthetic, donorAdvisedFund];

  if (isCustodialRothAvailableForRequest(custodian, requestContext)) {
    const existingCustodialRoth = base.find(
      (registration) => key(registration.name) === "custodial roth ira",
    );
    const roth = base.find(
      (registration) => key(registration.name) === "roth ira",
    );
    result.push(
      existingCustodialRoth || {
        ...(roth || {}),
        name: "Custodial Roth IRA",
        category: roth?.category || "Qualified Accounts",
        "Account Type": roth?.["Account Type"] || "Qualified",
        order: Number.isFinite(Number(roth?.order))
          ? Number(roth.order) + 0.1
          : 3.1,
      },
    );
  }

  return result;
}

export function getRequestCapabilities(requestType, existingRequestAction) {
  const isExisting = key(requestType) === "existing";
  const action = isExisting ? cleanText(existingRequestAction) : "";
  const isRiskToleranceUpdate = action === REQUEST_ACTIONS.RISK_TOLERANCE_UPDATE;
  const isModelChange = action === REQUEST_ACTIONS.MODEL_CHANGE;
  const isFeeUpdate = action === REQUEST_ACTIONS.FEE_UPDATE;
  const isReducedUpdate =
    isRiskToleranceUpdate || isModelChange || isFeeUpdate;

  return {
    isExisting,
    action,
    isReducedUpdate,
    asksFullAccountInfo: !isReducedUpdate,
    asksInvestmentDetails: !isReducedUpdate,
    asksRiskTolerance: !isModelChange && !isFeeUpdate,
    asksFees: !isRiskToleranceUpdate && !isModelChange,
    asksHouseholding: !isReducedUpdate,
    asksClientSignatures: !isModelChange,
    asksOptionalBondPreference: isRiskToleranceUpdate,
  };
}

export function isStrategyEligibleForRequest(
  strategy,
  requestType,
  existingRequestAction,
  strategyDisplayName = "",
) {
  const capabilities = getRequestCapabilities(
    requestType,
    existingRequestAction,
  );
  if (capabilities.action !== REQUEST_ACTIONS.MODEL_CHANGE) return true;
  return ![strategy, strategyDisplayName].some((candidate) => {
    const normalized = key(candidate)
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return false;
    const tokens = normalized.split(" ");
    const isFusionAlias = tokens.includes("fusion");
    const isPcsAlias =
      tokens.includes("pcs") ||
      normalized.includes("p c s") ||
      normalized.includes("private client strategy") ||
      normalized.includes("private client strategies");
    return isFusionAlias || isPcsAlias;
  });
}

export function getBondPreferencePayload(value, provided) {
  const normalized = cleanText(value);
  const isProvided =
    provided === true && ["Taxable", "Municipal"].includes(normalized);
  return {
    bondType: isProvided ? normalized : "",
    bondTypePreferenceProvided: isProvided,
  };
}

export function getConditionalStateReset({
  requestType,
  existingRequestAction,
  registration,
  custodian,
  jointClients,
  registrationTypes,
} = {}) {
  const request = getRequestCapabilities(requestType, existingRequestAction);
  const registrationRules = getRegistrationRules(registration, custodian);
  const detailKind = getRegistrationDetailKind(
    registration,
    registrationTypes,
  );
  const reset = {};

  if (!request.asksInvestmentDetails) {
    Object.assign(reset, {
      custodian: "",
      modelSelection: "",
      pathBondType: "",
      initialInvestmentAmount: "",
      accountNetWorthPct: "",
      accountIncomeNeed: "",
      accountIncomeAmount: "",
      accountIncomeStartDate: "",
      accountIncomeEndDate: "",
      accountIncomeFrequency: "",
      accountGoal: "",
      accountGoalOther: "",
      sourceOfFunds: [],
      sourceOfFundsText: {},
    });
  }

  if (!request.asksRiskTolerance) {
    Object.assign(reset, {
      capacityAnswers: {},
      willingnessAnswers: {},
      capacityTotal: 0,
      willingnessTotal: 0,
      combinedScore: 0,
      riskCategory: "",
      portfolioOverride: false,
      overrideReason: "",
      overrideOtherText: "",
    });
  }

  if (!request.asksOptionalBondPreference) {
    reset.bondTypePreferenceProvided = false;
  }
  if (detailKind !== REGISTRATION_DETAIL_KINDS.MINOR) {
    reset.custodianFullName = "";
    reset.relationshipToPrimary = "";
    reset.custodialAdultName = "";
  }
  if (detailKind !== REGISTRATION_DETAIL_KINDS.TRUST) reset.trustName = "";
  if (detailKind !== REGISTRATION_DETAIL_KINDS.ESTATE) {
    reset.decedentName = "";
    reset.decedentSsnLast4 = "";
    reset.relationshipToExecutor = "";
  }
  if (detailKind !== REGISTRATION_DETAIL_KINDS.BUSINESS) {
    reset.businessName = "";
    reset.businessEinLast4 = "";
  }
  if (detailKind !== REGISTRATION_DETAIL_KINDS.SOLO_K) {
    reset.soloKQuestionnaire = {
      ...EMPTY_SOLO_K_QUESTIONNAIRE,
      ownershipEvidence: [],
    };
  }
  if (!request.asksHouseholding || !registrationRules.allowsHouseholding) {
    Object.assign(reset, {
      householding: "",
      householdRefName: "",
      householdRefLast4: "",
      householdRef: "",
      householdReason: "",
      householdReasonType: "",
      householdReasonOther: "",
    });
  }
  if (
    !registrationRules.allowsSecondAccountHolder &&
    Array.isArray(jointClients)
  ) {
    const primary =
      jointClients.find((client) => client?.isPrimary) || jointClients[0];
    reset.jointClients = primary
      ? [{ ...primary, isPrimary: true }]
      : [];
  }

  return reset;
}
